import React, { useCallback, useEffect, useState } from 'react';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { GenericErrorMessage } from 'src/components/BeaconV2Components/common/CommonSnackbarMessages/GenericErrorMessage';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import { useBeaconNavigator } from 'src/components/BeaconV2Components/BeaconNavigation/useBeaconNavigator';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { SlLimitedInput } from 'src/components/BeaconV2Components/SlLimitedInput';
import { StyledFlexContainer } from './styledWrappers';
import { RuleConditions } from './RuleConditions';
import { RuleTimes } from './RuleTimes';
import { RuleDays } from './RuleDays';
import { MetricGrid } from './MetricGrid';
import color from 'src/utils/colors';
import RuleStore from './stores';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import { Box } from '@mui/system';
import { useAppSelector, useSnackbar } from 'src/utils/Hooks';
import { editScheduledAction, createScheduledAction, fetchScheduledAction, parseScheduledActionData } from './utils';
import useAdTargetData from './hooks';
import { deleteScheduleActions } from 'src/store/modules/adManager/adScheduledActionGroups/selectors';

const RulesDetails = () => {
  const {
    ruleName,
    setRuleName,
    rules,
    days,
    startTime,
    endTime,
    selectedCampaigns,
    selectedTargets,
    ruleStrategyMetric,
    ruleTarget,
    selectedTimeZone,
    editRuleId,
    removeEditRuleId,
    setRuleStoreData
  } = RuleStore((state) => ({
    ruleName: state.ruleName,
    setRuleName: state.setRuleName,
    rules: state.rules,
    days: state.days,
    startTime: state.startTime,
    endTime: state.endTime,
    selectedCampaigns: state.selectedCampaigns,
    selectedTargets: state.selectedTargets,
    ruleStrategyMetric: state.rules.ruleStrategyMetric,
    ruleTarget: state.rules.ruleTarget,
    selectedTimeZone: state.selectedTimeZone,
    editRuleId: state.editRuleId,
    removeEditRuleId: state.removeEditRuleId,
    setRuleStoreData: state.setRuleStoreData
  }));

  const [editData, setEditData] = useState(null);

  const { adTargetDocuments } = useAdTargetData(selectedTargets);

  const { showSnackbar } = useSnackbar();

  const { beaconNavigator } = useBeaconNavigator();
  const { platformType, retailerId } = useAppSelector((state) => ({
    platformType: state.retailer.platformType,
    retailerId: state.retailer.id
  }));

  const isEdit = !!editRuleId;

  useEffect(() => {
    if (editData) {
      const { data } = editData;
      const parsedData = parseScheduledActionData(data);
      setRuleStoreData(parsedData);
    }
  }, [editData, setRuleStoreData]);

  useEffect(() => {
    const fetchData = async () => {
      return fetchScheduledAction(editRuleId, platformType);
    };

    if (isEdit) {
      fetchData().then((data) => {
        setEditData(data);
      });
    }
  }, [editRuleId, isEdit, platformType]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      const fn = isEdit ? editScheduledAction : createScheduledAction;
      try {
        const res = await fn({
          platformType,
          ruleName,
          rules,
          days,
          startTime,
          endTime,
          selectedCampaigns,
          selectedTargets,
          ruleStrategyMetric,
          ruleTarget,
          selectedTimeZone,
          adTargetDocuments,
          retailerId,
          originRequests: editData?.data
        });

        const getUniqueAdTargets = (adTargets) => {
          return new Set(adTargets.map((target) => target.targetingText)).size;
        };

        const rulesImpacted =
          res.data[0]?.extendedAttributes?.adUpdateRequest?.extendedAttributes?.adCampaignsBudgetSetting?.length ??
          getUniqueAdTargets(res.data[0]?.extendedAttributes?.adUpdateRequest?.extendedAttributes?.adTargets);

        let ruleItemType = res.data[0]?.extendedAttributes?.adUpdateRequest?.extendedAttributes
          ?.adCampaignsBudgetSetting?.length
          ? 'campaign'
          : 'target';

        if (rulesImpacted > 1) {
          ruleItemType += 's';
        }

        showSnackbar({
          message: `You have successfully ${isEdit ? 'edited' : 'created'} a ${isEdit ? 'new' : ''
            } rule impacting ${rulesImpacted} ${ruleItemType}.`,
          type: 'success'
        });

        beaconNavigator.updateSearchParams({ tab: 'advertising', subtab: 'rulesSummary' });
        beaconNavigator.go();
      } catch (error) {
        showSnackbar({
          message: error.message,
          type: 'error'
        });
      }
    },
    [
      adTargetDocuments,
      beaconNavigator,
      days,
      editData,
      endTime,
      isEdit,
      platformType,
      retailerId,
      ruleName,
      ruleStrategyMetric,
      ruleTarget,
      rules,
      selectedCampaigns,
      selectedTargets,
      selectedTimeZone,
      showSnackbar,
      startTime
    ]
  );

  return (
    <BasePageContainer>
      <Box
        component="form"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%'
        }}
        onSubmit={handleSubmit}
        noValidate
      >
        <Text variant="h7">Rule Name</Text>
        <div style={{ marginBottom: '32px' }}>
          <SlLimitedInput
            placeholder="Name your advertising rule"
            required
            onChange={(e) => setRuleName(e.target.value)}
            characterLimit={100}
            value={ruleName}
            sx={{
              width: '100%',
              marginTop: '8px',
              padding: '10px 14px',
              boxSizing: 'border-box',
              '& .MuiInputBase-root': {
                borderRadius: '4px',
                border: `1px solid ${color.graysSecondary}`,
                backgroundColor: '#fff',
                height: '48px',
                fontSize: '14px',
                fontWeight: 400
              },
              '& .MuiInputBase-input.Mui-disabled': {
                WebkitTextFillColor: color.grey,
                opacity: '0.5'
              },
              '& .MuiFormLabel-root': {
                color: color.blueGrey
              }
            }}
          />
        </div>

        <StyledFlexContainer flexDirection="column" gap={40}>
          <RuleConditions />
          <RuleTimes />
          <RuleDays />
          <MetricGrid />
        </StyledFlexContainer>
        <Flex
          justifyContent="space-between"
          width="100%"
          sx={{
            marginTop: '64px'
          }}
        >
          <SlButton
            onClick={() => {
              removeEditRuleId();
              beaconNavigator.updateSearchParams({ tab: 'advertising', subtab: 'rulesSummary' });
              beaconNavigator.go();
            }}
          >
            Cancel
          </SlButton>
          <Flex gap={16}>
            {editData && (
              <SlButton
                onClick={async () => {
                  try {
                    await deleteScheduleActions(editData.data);
                    showSnackbar({
                      message: 'Rule deleted successfully',
                      type: 'success'
                    });
                    beaconNavigator.updateSearchParams({ tab: 'advertising', subtab: 'rulesSummary' });
                    beaconNavigator.go();
                  } catch (error) {
                    showSnackbar({
                      message: <GenericErrorMessage />,
                      type: 'error'
                    });
                  }
                }}
                buttonSx={{
                  backgroundColor: color.graysPrimary,
                  border: `1px solid ${color.graysSecondary}`
                }}
              >
                Delete
              </SlButton>
            )}
            <SlButton variant="contained" type="submit">
              Save
            </SlButton>
          </Flex>
        </Flex>
      </Box>
    </BasePageContainer>
  );
};

export default RulesDetails;
